import { Result } from "true-myth/result";
import { z } from "zod/mini";

import type { DeployEnvironment } from "./types";

interface EnvWithDeploy {
  DEPLOY_ENVIRONMENT: string;
}

export function envRefine<T extends EnvWithDeploy>(
  targetEnv: DeployEnvironment | DeployEnvironment[],
  validate: (env: T) => string | null
) {
  const targets = Array.isArray(targetEnv) ? targetEnv : [targetEnv];
  return z.refine((env: unknown) => {
    const e = env as T;
    if (!targets.includes(e.DEPLOY_ENVIRONMENT as DeployEnvironment)) {
      return true;
    }
    const error = validate(e);
    return error !== null ? { error } : true;
  });
}

/**
 * Require specified keys to be non-null in target deployment environments.
 * Simplified version of envRefine for the common null-check pattern.
 *
 * @example
 * BaseEnvironmentSchema.check(
 *   envRequire<BaseEnvironment>(["production", "pr-preview"], ["GIT_SHA", "SENTRY"])
 * )
 */
export function envRequire<T extends EnvWithDeploy>(
  targetEnv: DeployEnvironment | DeployEnvironment[],
  keys: (keyof T)[]
) {
  return envRefine<T>(targetEnv, (env) => {
    for (const key of keys) {
      if (env[key] === null) {
        return `${String(key)} is required`;
      }
    }
    return null;
  });
}

export interface EnvironmentError {
  message: string;
  issues: readonly { message?: string; path?: PropertyKey[] }[];
}

export function parseEnvironment<T extends z.ZodMiniType>(
  schema: T,
  raw: Record<string, unknown>
): Result<z.infer<T>, EnvironmentError> {
  const normalized = Object.fromEntries(
    Object.entries(raw).map(([k, v]) => [
      k,
      v === undefined || v === "" ? null : v,
    ])
  );
  const result = schema.safeParse(normalized);
  if (!result.success) {
    return Result.err({
      message: "Environment validation failed",
      issues: result.error.issues,
    });
  }
  return Result.ok(result.data);
}
